import axios from 'axios';
import { toast } from 'react-toastify';

const isNetworkError = (error) => {
  if (!error) return false;
  const code = error.code;
  const msg = error.message || '';
  return (
    code === 'ERR_NETWORK' ||
    code === 'ECONNREFUSED' ||
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    msg.includes('Network Error') ||
    msg.includes('Failed to fetch')
  );
};

// In development, use proxy (avoids CORS) - requests go to same origin, proxy forwards to API

const getBaseURL = () => {
  const application_mode = process.env.REACT_APP_APPLICATION_MODE;

  if (application_mode === 'development') {
    return process.env.REACT_APP_DEVELOPMENT_BASE_API_URL;
  }

  return process.env.REACT_APP_PRODUCTION_BASE_API_URL;
};

export const getApiBaseUrl = () => getBaseURL();

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 30000, // 30 seconds - increased for slow connections
});

const AUTH_PATH_SKIP_401 =
  /\/(login|auth\/login|forgot-password|forget-password|reset-password|password\/email|password\/reset)/i;

const clearAuthStorage = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('impersonating');
  localStorage.removeItem('impersonation_parent_token');
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Let the runtime set multipart boundary — never force Content-Type on FormData
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers && typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isNetworkError(error)) {
      const message =
        'Cannot connect to server. Please check your internet connection and ensure the API server is running.';
      error.apiMessage = message;
      toast.error(message, { toastId: 'network-error', autoClose: 5000 });
    } else if (error.response?.status === 401) {
      const requestUrl = String(error.config?.url || '');
      const hadToken = Boolean(localStorage.getItem('token'));
      const onLoginPage =
        typeof window !== 'undefined' &&
        /\/login/i.test(window.location.pathname || '');
      const isAuthEndpoint = AUTH_PATH_SKIP_401.test(requestUrl);

      // Failed login / password flows must not hard-redirect and wipe the form
      if (hadToken && !isAuthEndpoint && !onLoginPage) {
        clearAuthStorage();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
